// Written by Codex on 2026-10-19
// User prompt: Separate employee accommodations, consolidate leave submission, and add dashboard Clara.
// Written by Claude Code on 2026-02-24
// User prompt: Magic patterns UI overhaul - Sidebar with auth integration
// Updated by Claude Code on 2026-03-03
// User prompt: Implement role-based auth - remove role switcher
// Updated by Claude Code on 2026-05-10
// User prompt: clean up the sidebar — rectangular nav items, remove role badge box, simpler active state
// Updated by Claude Code on 2026-05-31
// User prompt: per-client module/product gating — filter HR nav by enabled modules
// Updated by Claude Code on 2026-10-23
// User prompt: open the 'me' tab from the profile in the bottom left instead of a sidebar item

import React from 'react';
import {
  LayoutDashboardIcon,
  UsersIcon,
  UserCogIcon,
  UserPlusIcon,
  CheckCircleIcon,
  LogOutIcon,
  Building2Icon,
  PlugZapIcon,
  SettingsIcon,
  ShieldCheckIcon,
  PlaneIcon,
  MapIcon,
  DollarSignIcon,
  CalculatorIcon,
  TrendingUpIcon,
  HomeIcon,
  TimerIcon,
  SparklesIcon,
  ZapIcon,
  AlertTriangleIcon,
  FileTextIcon,
  ChevronLeftIcon,
  TrendingDownIcon,
} from 'lucide-react';

import { MODULES } from '../config/modules';

export const SIDEBAR_WIDTH = 240;
export const SIDEBAR_WIDTH_COLLAPSED = 72;

// Nav items for roles other than HR (no module gating needed)
const personalItems = [
  { id: 'dashboard',       label: 'Home',         icon: HomeIcon   },
  { id: 'leave-status',    label: 'My Leave',     icon: PlaneIcon  },
  { id: 'accommodations', label: 'Accommodations', icon: ShieldCheckIcon },
  { id: 'pay-estimator',   label: 'Pay Planner',  icon: CalculatorIcon },
  { id: 'calendar',        label: 'Calendar',     icon: TimerIcon  },
];

const aiAssistantItem = { id: 'ai-assistant', label: 'Clara',      icon: SparklesIcon  };

// All possible HR nav items, each tagged with the module that controls them.
// Items without a moduleId are always shown (core: dashboard, people, connections, settings).
const ALL_HR_ITEMS = [
  { id: 'dashboard',         label: 'Dashboard',         icon: LayoutDashboardIcon, moduleId: null },
  { id: 'ai-assistant',      label: 'Clara',             icon: SparklesIcon,        moduleId: 'ai_assistant' },
  // Leave Hub carries a "New Request" tab: intake for a case HR opens on someone's behalf
  // — phoned in, mailed, or where the employee cannot file it. It used to sit here as its
  // own nav item, which split filing a case from triaging one. The 'new-leave' ROUTE still
  // exists (App.js) for the employee/manager sidebars and for the draft hand-off from
  // Plan Leave; it is only the duplicate HR sidebar entry that is gone.
  { id: 'leave-hub',         label: 'Leave Hub',         icon: PlaneIcon,           moduleId: 'leave_management' },
  { id: 'people',            label: 'People',            icon: UsersIcon,           moduleId: null },
  { id: 'onboarding',        label: 'Onboarding',        icon: UserPlusIcon,        moduleId: 'onboarding' },
  { id: 'accommodations',    label: 'Accommodations',    icon: ShieldCheckIcon,     moduleId: 'accommodations' },
  { id: 'incident-reports',  label: 'Incident Reports',  icon: AlertTriangleIcon,   moduleId: 'incident_reports' },
  { id: 'payroll',           label: 'Payroll',           icon: DollarSignIcon,      moduleId: 'payroll' },
  { id: 'pay-estimator',     label: 'Pay Planner',       icon: CalculatorIcon,      moduleId: null },
  { id: 'leakage',           label: 'Benefit Leakage',   icon: TrendingDownIcon,    moduleId: null },
  { id: 'roi-calculator',    label: 'ROI Calculator',    icon: DollarSignIcon,      moduleId: null },
  { id: 'workflows',         label: 'Workflows',         icon: ZapIcon,             moduleId: 'workflows' },
  { id: 'reports',           label: 'Reports',           icon: TrendingUpIcon,      moduleId: 'reports' },
  { id: 'famli-wage-report', label: 'FAMLI Wages',       icon: FileTextIcon,        moduleId: 'famli_wage_report' },
  { id: 'connections',       label: 'Connections',       icon: PlugZapIcon,         moduleId: null },
  { id: 'settings',          label: 'Settings',          icon: SettingsIcon,        moduleId: null },
];

const roleLabels = {
  hr:           'HR Admin',
  employee:     'Employee',
  manager:      'Manager',
  'super-admin': 'Super Admin',
};

// Written by Codex on 2026-10-21
// User prompt: Make navigation usable on different screen sizes and zoom levels.
function Sidebar({ activeRole, activeNav, onNavChange, user, dbUser, onLogout, enabledModuleIds, collapsed, onToggleCollapse, mobileOpen = false }) {
  const displayName = dbUser?.preferred_name || dbUser?.name || user?.displayName || user?.email?.split('@')[0] || 'User';
  const initials    = displayName.split(' ').map((w) => w[0]).join('').toUpperCase().slice(0, 2);
  // Employees and managers have the "Me" page; HR and super-admins have "My Profile".
  const profileNavId  = ['hr', 'super-admin'].includes(activeRole) ? 'profile' : 'me';
  const profileActive = activeNav === profileNavId;

  let sections;
  if (activeRole === 'hr') {
    const visibleItems = ALL_HR_ITEMS.filter((item) => {
      if (!item.moduleId) return true;
      if (!enabledModuleIds) return true;  // loading — optimistic show
      return enabledModuleIds.includes(item.moduleId);
    });
    sections = [
      { items: visibleItems },
      // HR administers everyone's leave and takes their own. Kept in a separate section so
      // "My Leave" is not mistaken for the org-wide Leave Hub above.
      { label: 'My leave', items: [
        { id: 'leave-status',   label: 'My Leave',    icon: PlaneIcon      },
        { id: 'leave-planning', label: 'Plan Leave',  icon: MapIcon        },
        { id: 'calendar',       label: 'Calendar',    icon: TimerIcon      },
      ]},
    ];
  } else if (activeRole === 'employee') {
    sections = [{ items: [personalItems[0], aiAssistantItem, ...personalItems.slice(1)] }];
  } else if (activeRole === 'manager') {
    sections = [{
      items: [
        { id: 'dashboard',  label: 'Home',      icon: HomeIcon        },
        aiAssistantItem,
        { id: 'approvals',      label: 'Approvals',    icon: CheckCircleIcon },
        // A manager takes leave as well as approves it — these are their own, not the team's.
        { id: 'leave-status',   label: 'My Leave',     icon: PlaneIcon       },
        { id: 'calendar',       label: 'Calendar',     icon: TimerIcon       },
        { id: 'accommodations', label: 'Accommodations', icon: ShieldCheckIcon },
        { id: 'pay-estimator',  label: 'Pay Planner',  icon: CalculatorIcon  },
      ],
    }];
  } else if (activeRole === 'super-admin') {
    sections = [
      { items: [
        { id: 'dashboard',    label: 'Overview',     icon: LayoutDashboardIcon },
        // Written by Codex on 2026-10-19
        // User prompt: Add Clara to Superadmin for client operations.
        aiAssistantItem,
        { id: 'clients',      label: 'Clients',      icon: Building2Icon       },
        { id: 'user-invites', label: 'Invite Users', icon: UserPlusIcon        },
      ]},
    ];
  } else {
    sections = [{ items: [...personalItems] }];
  }

  return (
    <aside
      id="app-sidebar"
      data-mobile-open={mobileOpen}
      className={`app-sidebar h-screen bg-zinc-950 flex flex-col fixed left-0 top-0 z-30 transition-[width] duration-200 ease-in-out ${
        collapsed ? 'w-[72px]' : 'w-[240px]'
      }`}
    >
      {/* Collapse/expand toggle */}
      <button
        onClick={onToggleCollapse}
        className="absolute -right-3 top-16 w-6 h-6 rounded-full bg-zinc-900 border border-white/10 flex items-center justify-center text-zinc-400 hover:text-white hover:bg-zinc-800 transition-colors shadow-md z-10"
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        <ChevronLeftIcon className={`w-3.5 h-3.5 transition-transform duration-200 ${collapsed ? 'rotate-180' : ''}`} />
      </button>

      {/* Logo */}
      <a
        href="/"
        className={`h-14 flex items-center gap-2.5 border-b border-white/[0.06] hover:opacity-80 transition-opacity ${
          collapsed ? 'justify-center px-0' : 'px-5'
        }`}
      >
        <img src="/logo-icon.png" alt="Certify" className="w-7 h-7 object-contain flex-shrink-0" />
        {!collapsed && <span className="text-sm font-bold text-white tracking-tight">Certify</span>}
      </a>

      {/* Navigation */}
      <nav className="flex-1 px-3 py-4 overflow-y-auto overflow-x-hidden space-y-6" role="navigation" aria-label="Main navigation">
        {sections.map((section, sectionIdx) => (
          <div key={sectionIdx}>
            {section.label && !collapsed && (
              <p className="px-3 pb-2 text-[10px] font-semibold text-zinc-500 uppercase tracking-widest">
                {section.label}
              </p>
            )}
            {section.label && collapsed && sectionIdx > 0 && (
              <div className="mx-3 mb-2 border-t border-white/[0.06]" />
            )}
            <div className="space-y-0.5">
              {section.items.map((item) => {
                const isActive = activeNav === item.id || (item.id === 'accommodations' && activeNav === 'request-accommodation') || (item.id === 'leave-status' && ['new-leave', 'leave-planning'].includes(activeNav));
                const Icon     = item.icon;
                return (
                  <button
                    key={item.id}
                    onClick={() => onNavChange(item.id)}
                    className={`w-full flex items-center py-2 text-sm transition-colors relative ${
                      collapsed ? 'justify-center px-0' : 'gap-3 px-3'
                    } ${
                      isActive
                        ? 'text-white font-semibold bg-white/[0.08] border-l-2 border-teal-500'
                        : 'text-zinc-400 hover:text-white hover:bg-white/[0.04] border-l-2 border-transparent font-medium'
                    }`}
                    aria-current={isActive ? 'page' : undefined}
                    aria-label={item.label}
                    title={collapsed ? item.label : undefined}
                  >
                    <Icon className="w-4 h-4 flex-shrink-0" />
                    {!collapsed && <span>{item.label}</span>}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      {/* User section */}
      <div className="px-3 pb-4 pt-3 border-t border-white/[0.06]">
        <div className={`flex items-center ${collapsed ? 'justify-center' : 'gap-1'}`}>
          {/* The profile doubles as the link to the user's own page, so it needs no sidebar item */}
          <button
            onClick={() => onNavChange(profileNavId)}
            className={`flex items-center min-w-0 py-1.5 rounded-md transition-colors hover:bg-white/[0.04] ${
              collapsed ? 'justify-center px-1' : 'flex-1 gap-3 px-3 text-left'
            } ${profileActive ? 'bg-white/[0.08]' : ''}`}
            aria-current={profileActive ? 'page' : undefined}
            aria-label={`${displayName} — view your profile`}
            title={collapsed ? displayName : 'View your profile'}
          >
            {dbUser?.avatar_url ? (
              <img src={dbUser.avatar_url} alt="" className="w-7 h-7 rounded-full object-cover flex-shrink-0" />
            ) : (
              <div className="w-7 h-7 rounded-full bg-teal-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                {initials}
              </div>
            )}
            {!collapsed && (
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-white truncate">{displayName}</p>
                <p className="text-[10px] text-zinc-500">{roleLabels[activeRole] || 'Employee'}</p>
              </div>
            )}
          </button>
          {!collapsed && (
            <>
              <button
                onClick={onLogout}
                className="w-7 h-7 flex items-center justify-center text-zinc-500 hover:text-white transition-colors flex-shrink-0"
                aria-label="Sign out"
                title="Sign out"
              >
                <LogOutIcon className="w-3.5 h-3.5" />
              </button>
            </>
          )}
        </div>
        {collapsed && (
          <button
            onClick={onLogout}
            className="w-full mt-2 flex items-center justify-center py-1.5 text-zinc-500 hover:text-white transition-colors"
            aria-label="Sign out"
            title="Sign out"
          >
            <LogOutIcon className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </aside>
  );
}

export default Sidebar;
